// services/MultipartUploadService.js

import axios from "axios";
import { Api_Url } from "../utils/ApiUrl";

class MultipartUploadService {
  constructor(config = {}) {
    this.baseURL = config.baseURL || "";
    this.chunkSize = config.chunkSize || 1024 * 1024 * 5; // 5MB minimum for AWS
    this.threadsQuantity = Math.min(config.threadsQuantity || 5, 15);
    this.getAuthToken =
      config.getAuthToken || (() => localStorage.getItem("accessToken"));
    this.transformUrl = config.transformUrl || ((url) => url);

    this.api = axios.create({
      baseURL: this.baseURL,
    });

    // Add auth interceptor
    this.api.interceptors.request.use((config) => {
      const token = this.getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async uploadFile(file, options = {}) {
    const {
      destination = "project",
      onProgress,
      onPartComplete,
      signal, // AbortController signal
    } = options;

    this.aborted = false;
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = [];
    this.uploadedParts = [];

    try {
      // Step 1: Initialize upload
      const { fileId, fileKey } = await this.initializeUpload(
        file,
        destination
      );

      // Step 2: Get presigned URLs
      const numberOfParts = Math.ceil(file.size / this.chunkSize);
      const parts = await this.getPresignedUrls(fileKey, fileId, numberOfParts);

      // Step 3: Upload parts
      await this.uploadParts(file, parts, onProgress, onPartComplete, signal);

      // Step 4: Finalize upload
      const fileUrl = await this.finalizeUpload(
        fileKey,
        fileId,
        this.uploadedParts
      );

      // Transform URL if needed (e.g., S3 to CloudFront)
      const finalUrl = this.transformUrl(fileUrl);

      return {
        success: true,
        fileUrl: finalUrl,
        fileKey,
        fileId,
      };
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Upload cancelled");
      }
      throw error;
    }
  }

  async initializeUpload(file, destination) {
    const response = await this.api.post(
      `${Api_Url}/uploads/multipart-upload/init`,
      {
        fileName: file.name,
        destination,
      }
    );

    return {
      fileId: response.data.data.fileId,
      fileKey: response.data.data.fileKey,
    };
  }

  async getPresignedUrls(fileKey, fileId, numberOfParts) {
    const response = await this.api.post(
      `${Api_Url}/uploads/multipart-upload/presigned-urls`,
      {
        fileKey,
        fileId,
        parts: numberOfParts,
      }
    );

    return response.data.data.parts;
  }

  async uploadParts(file, parts, onProgress, onPartComplete, signal) {
    const uploadQueue = [...parts];
    const activeUploads = new Map();
    let completedSize = 0;

    const uploadPart = async (part) => {
      const start = (part.PartNumber - 1) * this.chunkSize;
      const end = Math.min(start + this.chunkSize, file.size);
      const chunk = file.slice(start, end);

      try {
        const response = await fetch(part.signedUrl, {
          method: "PUT",
          body: chunk,
          signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload part ${part.PartNumber}`);
        }

        const etag = response.headers.get("ETag")?.replaceAll('"', "");

        this.uploadedParts.push({
          PartNumber: part.PartNumber,
          ETag: etag,
        });

        completedSize += chunk.size;
        const progress = Math.round((completedSize / file.size) * 100);
        onProgress?.(progress);
        onPartComplete?.(part.PartNumber, parts.length);

        return { success: true, partNumber: part.PartNumber };
      } catch (error) {
        throw error;
      }
    };

    // Upload parts with concurrency control
    while (uploadQueue.length > 0 || activeUploads.size > 0) {
      // Start new uploads up to thread limit
      while (
        uploadQueue.length > 0 &&
        activeUploads.size < this.threadsQuantity
      ) {
        const part = uploadQueue.shift();
        const uploadPromise = uploadPart(part)
          .then((result) => {
            activeUploads.delete(part.PartNumber);
            return result;
          })
          .catch((error) => {
            activeUploads.delete(part.PartNumber);
            throw error;
          });

        activeUploads.set(part.PartNumber, uploadPromise);
      }

      // Wait for at least one upload to complete
      if (activeUploads.size > 0) {
        await Promise.race(activeUploads.values());
      }
    }

    // Sort uploaded parts by part number
    this.uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);
  }

  async finalizeUpload(fileKey, fileId, parts) {
    const response = await this.api.patch(
      `${Api_Url}/uploads/multipart-upload/finalize`,
      {
        fileKey,
        fileId,
        parts,
      }
    );

    return response.data.data.fileUrl;
  }
}

export default MultipartUploadService;
